import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { Calendar, Plus, Edit2, Trash2, Star, CalendarDays, GripVertical } from 'lucide-react';
import Navbar from '../components/Navbar';
import { useAuth } from '../lib/auth';
import api from '../lib/api';

const TermDates = () => {
  const { user } = useAuth();
  const isAdminOrStaff = user?.role === 'admin' || user?.role === 'staff';
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    dates: '',
    type: 'term', // 'term', 'event', 'holiday', 'show'
    description: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await api.get('/academy-info/term-events');
      setEvents(res.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      // Set default events if API not ready
      setEvents(getDefaultEvents());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultEvents = () => [
    { id: '1', title: 'January Classes', dates: 'Jan 10th, 17th, 24th, 31st', type: 'term', description: '' },
    { id: '2', title: 'February Classes (Part 1)', dates: 'Feb 7th, 14th', type: 'term', description: '' },
    { id: '3', title: 'Half Term Break', dates: 'Feb 15th - 27th', type: 'holiday', description: 'No classes during half term' },
    { id: '4', title: 'February/March Classes', dates: 'Feb 28th, Mar 7th, 14th, 21st, 28th', type: 'term', description: '' },
    { id: '5', title: 'April Classes', dates: 'April 4th, 18th, 25th', type: 'term', description: 'Easter break April 11th' },
    { id: '6', title: 'May Classes', dates: 'May 2nd, 9th, 16th, 23rd', type: 'term', description: '' },
    { id: '7', title: 'June Classes', dates: 'Jun 13th, 20th, 27th', type: 'term', description: '' },
    { id: '8', title: 'Summer Show Weekend', dates: '2nd & 3rd July', type: 'show', description: 'Annual summer showcase performance!' },
    { id: '9', title: 'July Classes', dates: 'Jul 11th, 18th', type: 'term', description: '' },
    { id: '10', title: 'Summer Bootcamp', dates: '27th - 31st July', type: 'event', description: 'Intensive week of workshops and training' },
  ];

  const handleSaveEvent = async () => {
    if (!eventForm.title || !eventForm.dates) {
      toast.error('Please fill in title and dates');
      return;
    }

    try {
      if (editingEvent) {
        await api.put(`/academy-info/term-events/${editingEvent.id}`, eventForm);
        toast.success('Event updated');
      } else {
        await api.post('/academy-info/term-events', eventForm);
        toast.success('Event added');
      }
      fetchEvents();
      setEditModalOpen(false);
      setEditingEvent(null);
      setEventForm({ title: '', dates: '', type: 'term', description: '' });
    } catch (error) {
      console.error('Failed to save event:', error);
      toast.error('Failed to save event');
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    
    try {
      await api.delete(`/academy-info/term-events/${id}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event');
    }
  };

  const openEditModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setEventForm({
        title: event.title,
        dates: event.dates,
        type: event.type,
        description: event.description || ''
      });
    } else {
      setEditingEvent(null);
      setEventForm({ title: '', dates: '', type: 'term', description: '' });
    }
    setEditModalOpen(true);
  };

  const [dragIndex, setDragIndex] = useState(null);

  const handleDrop = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) { setDragIndex(null); return; }
    const reordered = [...events];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setEvents(reordered);
    setDragIndex(null);
    try {
      await api.post('/academy-info/term-events/reorder', {
        ordered_ids: reordered.map((e) => e.id).filter(Boolean),
      });
      toast.success('Order saved');
    } catch (error) {
      toast.error('Failed to save order');
      fetchEvents();
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'show':
        return 'bg-[#FF007F]/20 text-[#FF007F] border-[#FF007F]/30';
      case 'event':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'holiday':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default:
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'show': return 'Show';
      case 'event': return 'Special Event';
      case 'holiday': return 'Holiday';
      default: return 'Term Dates';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="term-dates-page">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-gradient-to-b from-[#1a1a2e] to-[#0a0a0a] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF007F] to-purple-600 rounded-xl flex items-center justify-center">
                  <CalendarDays className="w-6 h-6 text-white" />
                </div>
                Term Dates 2025
              </h1>
              <p className="text-white/60 mt-2">Important dates and events for this academic year</p>
            </div>
            {isAdminOrStaff && (
              <Button 
                onClick={() => openEditModal()}
                className="bg-[#FF007F] hover:bg-[#D6006A] text-white font-bold"
                data-testid="add-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event, index) => (
              <Card 
                key={event.id || index} 
                draggable={isAdminOrStaff}
                onDragStart={() => setDragIndex(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(index)}
                onDragEnd={() => setDragIndex(null)}
                className={`bg-white/5 border-white/10 hover:border-white/20 transition-all ${event.type === 'show' ? 'ring-1 ring-[#FF007F]/50' : ''} ${dragIndex === index ? 'opacity-40' : ''} ${isAdminOrStaff ? 'cursor-grab active:cursor-grabbing' : ''}`}
                data-testid={`event-${event.id || index}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      {isAdminOrStaff && (
                        <GripVertical className="w-5 h-5 text-white/30 mt-3.5 flex-shrink-0" data-testid={`drag-handle-${event.id || index}`} />
                      )}
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getTypeStyles(event.type)}`}>
                        {event.type === 'show' ? (
                          <Star className="w-6 h-6" />
                        ) : (
                          <Calendar className="w-6 h-6" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-white">{event.title}</h3>
                          <Badge className={`text-xs ${getTypeStyles(event.type)}`}>
                            {getTypeLabel(event.type)}
                          </Badge>
                        </div>
                        <p className="text-[#FF007F] font-medium">{event.dates}</p>
                        {event.description && (
                          <p className="text-white/60 text-sm mt-2">{event.description}</p>
                        )}
                      </div>
                    </div>
                    {isAdminOrStaff && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(event)}
                          className="text-white/60 hover:text-white hover:bg-white/10"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteEvent(event.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-[#FF007F]/10 border-[#FF007F]/30 mt-8">
          <CardContent className="p-5">
            <p className="text-white/80 text-sm">
              <strong className="text-[#FF007F]">Note:</strong> All classes take place on Saturdays unless otherwise stated. 
              Please arrive 10 minutes early for your session. For any changes or cancellations, 
              please check your email or contact us at <a href="mailto:theteam@upaschool.com" className="text-[#FF007F] hover:underline">theteam@upaschool.com</a>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#FF007F]" />
              {editingEvent ? 'Edit Event' : 'Add Event'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-white/70 mb-1 block">Title *</label>
              <Input
                placeholder="e.g., January Classes"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="event-title"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Dates *</label>
              <Input
                placeholder="e.g., Jan 10th, 17th, 24th, 31st"
                value={eventForm.dates}
                onChange={(e) => setEventForm({ ...eventForm, dates: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="event-dates"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Type</label>
              <select
                value={eventForm.type}
                onChange={(e) => setEventForm({ ...eventForm, type: e.target.value })}
                className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2"
                data-testid="event-type"
              >
                <option value="term">Term Dates</option>
                <option value="event">Special Event</option>
                <option value="show">Show</option>
                <option value="holiday">Holiday</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Description (optional)</label>
              <Textarea
                placeholder="Additional details..."
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white resize-none"
                rows={2}
                data-testid="event-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="border-white/10 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={handleSaveEvent} className="bg-[#FF007F] hover:bg-[#D6006A] text-white" data-testid="save-event-btn">
              {editingEvent ? 'Update' : 'Add'} Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TermDates;
